import DatePickerBox from "controls/datePickerBox/datePickerBox";
import { observer } from "mobx-react-lite";
import React, { FunctionComponent, useEffect } from "react";
import { Alert, Col, Form } from "react-bootstrap";
import ReactMarkdown from "react-markdown";
import { dateStringWithServerZone, disabledDaysRange, maxValidSince, minValidSince } from "utils/dateUtils";
import ChargeManagementValidityViewModel from "viewModels/chargeManagement/chargeManagementEdit/chargeManagementValidityViewModel";
import { registerView, View } from "@frui.ts/views/dist";
import { useIdleTimer } from "react-idle-timer";
import { isSafari9OrLess } from "utils/iOS";
import AlertWithInfoIcon from "views/common/alertWithInfoIcon/alertWithInfoIcon";
import { startOfCzechDay, utcToCZZonedTime } from "../../../utils/dateUtils";
import { startOfDay, subDays, endOfDay } from "date-fns";

const ChargeManagementValidityView: FunctionComponent<{
  vm: ChargeManagementValidityViewModel;
}> = observer(({ vm }) => {
  const taErrorValidity = (code: string) => {
    if (["min", "max"].includes(code)) {
      if (code === "max" && vm.postponedValidityRule?.postponedValidityMaxDays) {
        return vm.localizationService
          .translateAttribute("validSince", code)
          .replace("%amount%", `${vm.postponedValidityRule.postponedValidityMaxDays}`);
      }
      return vm.localizationService.translateAttribute("validity", code).replace("%amount%", `${30}`);
    } else if (code === "invalidDateFormat") {
      return vm.localizationService.translateGeneral(code);
    } else {
      return code;
    }
  };

  const { getRemainingTime } = useIdleTimer({
    onIdle: vm.handleIdle,
    onActive: vm.clearTimeoutInterval,
    timeout: vm.IDLE_TIMER_TIMEOUT,
    debounce: vm.IDLE_TIMER_DEBOUNCE,
  });

  useEffect(() => {
    const interval = setInterval(() => {
      Math.ceil(getRemainingTime() / 1000) * 1000 + vm.IDLE_PROMPT_TIMEOUT;
    }, 1000);
    return () => clearInterval(interval);
  }, []);
  const datePickerLocalization = vm.localizationService.datePickerLocalization;
  const placeholder = vm.localizationService.translateGeneral("datepicker.placeholder");
  const disabledDays = disabledDaysRange(
    vm.serverTime,
    vm.charge?.chargeOrderConfirmedAt ? utcToCZZonedTime(vm.charge?.chargeOrderConfirmedAt) : new Date(),
    vm.postponedValidityRule?.postponedValidityMaxDays
  );
  let fromMonth = isSafari9OrLess ? startOfDay(disabledDays.before) : minValidSince();
  let toMonth = disabledDays.after
    ? endOfDay(disabledDays.after)
    : maxValidSince(fromMonth, vm.postponedValidityRule?.postponedValidityMaxDays);
  if (vm.charge?.newValidSince) {
    if (vm.charge?.newValidSince < fromMonth) {
      fromMonth = vm.charge?.newValidSince;
    } else if (vm.charge?.newValidSince > toMonth) {
      toMonth = vm.charge?.newValidSince;
    }
  }

  return (
    <>
      <Form.Row className="align-items-baseline">
        <Form.Group as={Col} sm={6}>
          <Form.Label>{vm.translate("validity.oldValidity")}</Form.Label>
          <Form.Control value={vm.validSince} disabled={true} />
        </Form.Group>
        <Form.Group as={Col} sm={6}>
          <Form.Label>{vm.translate("validity.newValidity")}</Form.Label>
          <DatePickerBox
            target={vm.charge}
            property="newValidSince"
            placeholder={placeholder}
            disabledDays={disabledDays}
            fromMonth={subDays(fromMonth, 1)}
            toMonth={toMonth}
            today={startOfDay(vm.serverTime)}
            errorMessage={vm.sameValidSinceError}
            datePickerLocalization={datePickerLocalization}
            errorLocalization={taErrorValidity}
            onValueChanged={vm.onNewValidityChanged}
            forSale={true}
          />
        </Form.Group>
      </Form.Row>
      <div className="mt-n3 mb-5">
        {vm.isSingleCharTypeToday && (
          <AlertWithInfoIcon variant="warning-dark">
            <span>{vm.translate("isSingleCharTypeToday")}</span>
          </AlertWithInfoIcon>
        )}
        {vm.isDifferentTimeZone && (
          <AlertWithInfoIcon variant="warning">
            <span>{vm.translate("differentTimeZone")}</span>
          </AlertWithInfoIcon>
        )}
        {vm.validChargesInfo && (
          <Alert variant="warning" className="border-none">
            <strong className="font-size-sm">
              <ReactMarkdown className="markdown">{vm.validChargesInfo}</ReactMarkdown>
            </strong>
          </Alert>
        )}
        {vm.exemptionInfo && (
          <Alert variant="warning" className="border-none">
            <strong className="font-size-sm">{vm.exemptionInfo}</strong>
          </Alert>
        )}
      </div>
      {vm.emailInputViewModel && <View vm={vm.emailInputViewModel} />}
    </>
  );
});

registerView(ChargeManagementValidityView, ChargeManagementValidityViewModel);
